/**
 * SIGHTS LAB — chapter 11, "Celestial Sights in Practice", and what a sight actually gives you.
 *
 * FOUNDER's overlay list: "Celestial sights: GP of the celestial body, observer, altitude circle,
 * intercept."
 *
 * ── THE IDEA THAT IS ALWAYS TAUGHT LAST AND SHOULD BE TAUGHT FIRST ────────────────────────────
 * A single sextant altitude does not give you a position. It gives you a CIRCLE — every place on
 * Earth where that body stands at that altitude — centred on the body's geographical position, with
 * an angular radius of exactly the zenith distance, 90° − altitude.
 *
 * That circle is enormous: a body 40° up puts you somewhere on a circle 3,000 miles in radius. A
 * candidate who has seen it drawn on a globe understands immediately why one sight is a position
 * LINE and not a fix, why two are needed, and why the tiny straight line he draws on the chart is a
 * short arc of something vast. Told as a definition, it stays a definition.
 *
 * ── EVERY NUMBER IS COMPUTED OR CITED, NONE INVENTED ──────────────────────────────────────────
 * The body's GHA and declination come from /api/almanac — our own, VSOP87, gated in nav-tables.mjs
 * against an independent implementation to better than 0.2' (sun 0.013'). The GP follows directly:
 * its latitude IS the declination and its longitude IS the GHA, west. Hc and Zn are the spherical
 * triangle, worked here in full.
 *
 * ── AND ONE HONEST LIMIT, STATED ──────────────────────────────────────────────────────────────
 * The altitude shown is the CALCULATED one for the position on screen. A real sight is an OBSERVED
 * altitude corrected for index error, dip, refraction, semi-diameter and parallax, and the intercept
 * is the difference between the two. Those corrections are a separate chapter and are not applied
 * here — so this lab draws the geometry of the method, and does not pretend to have taken a sight.
 */

import { useState } from 'react';
import NavSphere, { dm } from './navsphere.jsx';
import { Value, useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';
import { localAlmanac } from './offline/almanac.mjs';

/* THE SIGHT STILL WORKS WITH NO SIGNAL, which is the whole point of a sight. The ephemeris travels
   with the app, so when the fetch fails the same VSOP87 series that answer on the server answer
   here. Module-level, so the reference is stable across renders. */
const almanacOffline = (vars) => localAlmanac(vars || {});

const RAD = Math.PI / 180;
const W_ALM = { slug: 'almanac-now', layout: 'auto-table',
  wire: { kind: 'rest', route: '/api/almanac', method: 'GET', field: 'almanac' } };
/* The SAME endpoint at a later hour. /api/almanac takes ?date=, so the second sight comes from the
   same gated ephemeris rather than from an assumed 15°/hour — which would be inventing a small
   ephemeris on top of a validated one. */
const W_ALM2 = { slug: 'almanac-later', layout: 'auto-table',
  wire: { kind: 'rest', route: '/api/almanac', method: 'GET', field: 'almanac' } };

const wrap = (d) => ((d + 540) % 360) - 180;

/* An altitude is not a coordinate — it has no hemisphere. dm() would stamp an E/W on it. */
function altdm(deg) {
  const a = Math.abs(deg), d = Math.floor(a), m = (a - d) * 60;
  return `${String(d).padStart(2, '0')}° ${m.toFixed(1).padStart(4, '0')}′`;
}

/**
 * THE MERIDIAN, SEEN EDGE-ON — the figure every noon-sight explanation draws on a napkin and no
 * textbook animates. One circle: your meridian. Everything on it is an ARC of that circle, which is
 * the entire reason the quantities add instead of needing a triangle solved.
 *
 * Drawn as PAPER, not chrome: it is the subject, so it does not follow the theme (paper/chrome
 * ruling, 2026-08-10). A diagram whose meaning is carried by position and arc cannot be inverted.
 */
function MeridianFigure({ lat, dec, zd }) {
  const S = 300, C = S / 2, R = 96, PAD = 78;   // PAD: the labelled arcs live outside R
  // Screen angle for a latitude on the meridian circle: north up, so 90°N is straight up.
  const pt = (angDeg, r = R) => [C + r * Math.sin(angDeg * RAD), C - r * Math.cos(angDeg * RAD)];
  const you = pt(90 - lat);            // observer on the circle
  const gp = pt(90 - dec);             // sub-solar point on the same meridian
  const zen = pt(90 - lat, R + 34);    // his zenith, radially outward
  const eq = pt(90);                   // the Equator
  const arc = (a, b, r, sweep = 0) =>
    `M ${pt(a, r)[0]} ${pt(a, r)[1]} A ${r} ${r} 0 0 ${sweep} ${pt(b, r)[0]} ${pt(b, r)[1]}`;
  const latArc = arc(90, 90 - lat, R + 16, lat > 0 ? 0 : 1);
  const zdArc = arc(90 - lat, 90 - dec, R + 52, lat > dec ? 1 : 0);

  return (
    <svg viewBox={`${-PAD} 0 ${S + PAD * 2} ${S}`} width="100%" style={{ maxWidth: 420, display: 'block', margin: '0 auto' }}>
      <circle cx={C} cy={C} r={R} fill="#0d2137" stroke="#2d5f86" strokeWidth="1.5" />
      {/* the axis and the Equator plane */}
      <line x1={C} y1={C - R - 22} x2={C} y2={C + R + 22} stroke="#2d5f86" strokeDasharray="4 4" />
      <line x1={C - R - 8} y1={C} x2={C + R + 8} y2={C} stroke="#4fd08a" strokeWidth="1.5" />
      <text x={C + R + 12} y={C + 4} fill="#4fd08a" fontSize="11">Equator</text>
      {/* LATITUDE — an arc of this meridian, Equator to observer */}
      <path d={latArc} fill="none" stroke="#5ec8f0" strokeWidth="3" />
      <text x={pt(90 - lat / 2, R - 26)[0]} y={pt(90 - lat / 2, R - 26)[1]}
            fill="#5ec8f0" fontSize="12" textAnchor="middle">lat {Math.abs(lat).toFixed(1)}°</text>
      {/* ZENITH DISTANCE — an arc of the SAME meridian, observer to the sun's GP */}
      <path d={zdArc} fill="none" stroke="#ffd479" strokeWidth="3" />
      <text x={pt(90 - (lat + dec) / 2, R + 66)[0]} y={pt(90 - (lat + dec) / 2, R + 66)[1]}
            fill="#ffd479" fontSize="12" textAnchor="middle">ZD {zd.toFixed(1)}°</text>
      {/* the ray to the zenith, and the sun's ray straight down onto its GP */}
      <line x1={you[0]} y1={you[1]} x2={zen[0]} y2={zen[1]} stroke="#c98cf1" strokeWidth="1.5" />
      <text x={zen[0]} y={zen[1] - 6} fill="#c98cf1" fontSize="11" textAnchor="middle">zenith</text>
      <line x1={C} y1={C} x2={pt(90 - dec, R + 46)[0]} y2={pt(90 - dec, R + 46)[1]}
            stroke="#ffd479" strokeWidth="1" strokeDasharray="3 3" />
      <circle cx={you[0]} cy={you[1]} r="5" fill="#5ec8f0" stroke="#fff" strokeWidth="1.5" />
      <text x={you[0] - 12} y={you[1] - 8} fill="#eaf2f8" fontSize="12" textAnchor="end">you</text>
      <circle cx={gp[0]} cy={gp[1]} r="5" fill="#ffd479" stroke="#fff" strokeWidth="1.5" />
      <text x={gp[0] + 12} y={gp[1] + 4} fill="#ffd479" fontSize="12">sun&rsquo;s GP</text>
      <circle cx={eq[0]} cy={eq[1]} r="3.5" fill="#4fd08a" />
    </svg>
  );
}

export default function SightsLab() {
  const [obs, setObs] = useState({ lat: 45, lon: -30 });
  const [body, setBody] = useState('sun');
  const [gap, setGap] = useState(0);
  const [tmin, setTmin] = useState(0);          // minutes either side of meridian passage            // hours to the second sight; 0 = single sight
  const almq = useWidgetData(W_ALM, {}, { baseUrl: API, plan, offline: almanacOffline });
  const alm = almq.data;
  const b = alm?.[body];

  const laterISO = alm?.utc
    ? new Date(new Date(alm.utc).getTime() + gap * 3600e3).toISOString() : null;
  const alm2q = useWidgetData(W_ALM2, laterISO ? { date: laterISO } : {},
                              { baseUrl: API, plan, enabled: gap > 0 && Boolean(laterISO),
                                offline: almanacOffline });
  const b2 = gap > 0 ? alm2q.data?.[body] : null;

  if (almq.envelope || (!almq.loading && !b)) {
    return (
      <div className="g-glass shell-panel lab">
        <div className="g-label">Sights lab</div>
        <div style={{ marginTop: 'var(--g-3)' }}>
          <Value of={almq.envelope || refused('the almanac returned no body to work with')} />
        </div>
      </div>
    );
  }

  // THE GEOGRAPHICAL POSITION. Its latitude IS the declination; its longitude IS the GHA, west.
  const gpLat = b?.dec ?? 0;
  const gpLon = wrap(-(b?.gha ?? 0));

  // The spherical triangle: sin Hc = sin lat · sin dec + cos lat · cos dec · cos LHA
  const lha = wrap((b?.gha ?? 0) + obs.lon);
  const sinHc = Math.sin(obs.lat * RAD) * Math.sin(gpLat * RAD)
              + Math.cos(obs.lat * RAD) * Math.cos(gpLat * RAD) * Math.cos(lha * RAD);
  const Hc = Math.asin(Math.max(-1, Math.min(1, sinHc))) / RAD;
  const zd = 90 - Hc;                                   // zenith distance = the circle's radius
  const zdNm = zd * 60;

  // Azimuth, by the same triangle
  const cosZ = (Math.sin(gpLat * RAD) - Math.sin(obs.lat * RAD) * sinHc)
             / (Math.cos(obs.lat * RAD) * Math.cos(Hc * RAD));
  const Z = Math.acos(Math.max(-1, Math.min(1, cosZ))) / RAD;
  const Zn = lha > 0 ? 360 - Z : Z;

  const up = Hc > 0;

  /* Time to meridian passage: LHA reaches 360°/0° at 15.041° per hour (the MEAN sun's rate — the
     apparent sun runs a little fast or slow, which is the equation of time the almanac already
     carries as eot_min). */
  const merHrs = (((360 - lha) % 360) / 15.041);
  /* ALTITUDE AS A FUNCTION OF TIME AROUND MERIDIAN PASSAGE. This is the whole of the noon sight:
     the sun climbs, then FLATTENS, then falls — and the flattening is not a figure of speech, it is
     a cosine at its maximum, where the rate of change is exactly zero. */
  const altAt = (mins) => {
    const l = wrap(15.041 * (mins / 60));       // LHA that many minutes from transit
    const sh = Math.sin(obs.lat * RAD) * Math.sin(gpLat * RAD)
             + Math.cos(obs.lat * RAD) * Math.cos(gpLat * RAD) * Math.cos(l * RAD);
    return Math.asin(Math.max(-1, Math.min(1, sh))) / RAD;
  };
  const altPeak = altAt(0);
  const altNow = altAt(tmin);
  const dropMin = (altPeak - altNow) * 60;      // minutes of ARC below the maximum
  /* How much she moves in the two minutes before transit — the number that settles the argument. */
  const twoMin = (altAt(0) - altAt(-2)) * 60;
  const merUTC = alm?.utc
    ? new Date(new Date(alm.utc).getTime() + merHrs * 3600e3).toISOString().slice(11, 16)
    : '—';

  /* THE SECOND SIGHT. Same body, later hour — which is the whole answer to "why does the sun need
     two readings". By day the sun is the only body up, so the second position line cannot come from
     a different body; it has to come from a different TIME. */
  const gp2Lat = b2?.dec ?? null;
  const gp2Lon = b2 ? wrap(-(b2.gha)) : null;
  let Hc2 = null, zd2 = null, Zn2 = null, cut = null;
  if (b2) {
    const lha2 = wrap(b2.gha + obs.lon);
    const s2 = Math.sin(obs.lat * RAD) * Math.sin(gp2Lat * RAD)
             + Math.cos(obs.lat * RAD) * Math.cos(gp2Lat * RAD) * Math.cos(lha2 * RAD);
    Hc2 = Math.asin(Math.max(-1, Math.min(1, s2))) / RAD;
    zd2 = 90 - Hc2;
    const cz2 = (Math.sin(gp2Lat * RAD) - Math.sin(obs.lat * RAD) * s2)
              / (Math.cos(obs.lat * RAD) * Math.cos(Hc2 * RAD));
    const Z2 = Math.acos(Math.max(-1, Math.min(1, cz2))) / RAD;
    Zn2 = lha2 > 0 ? 360 - Z2 : Z2;
    // The CUT: how squarely the two position lines cross. It is the azimuth change, and it is the
    // only thing that makes a second sight worth taking.
    cut = Math.abs(((Zn2 - Zn + 540) % 360) - 180);
    if (cut > 90) cut = 180 - cut;
  }

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Sights lab · what one altitude really tells you</div>
      <h2 className="g-h2 lab-h">A sight gives you a circle, not a position</h2>
      <p className="g-body">
        The <b className="lab-key lab-gp">geographical position</b> is the spot on Earth with the body
        exactly overhead. Every place where it stands at your altitude lies on a
        <b className="lab-key lab-zen"> circle</b> around it. Drag yourself around that circle and the
        altitude does not change — which is precisely why one sight cannot fix you.
      </p>

      <div className="lab-globe">
        <NavSphere height={340}
          overlays={{ graticule: true, equator: true, primeMeridian: true,
                      circles: [
                        ...(up ? [{ lat: gpLat, lon: gpLon, radius: zd, colour: '#ffd479', width: 2.2 }] : []),
                        ...(b2 && Hc2 > 0
                            ? [{ lat: gp2Lat, lon: gp2Lon, radius: zd2, colour: '#38b6e8', width: 2.2 }] : []),
                      ] }}
          marks={[{ lat: gpLat, lon: gpLon, label: `${body} GP`, arcs: false, colour: '#ffd479' },
                  ...(b2 ? [{ lat: gp2Lat, lon: gp2Lon, label: `GP +${gap}h`, arcs: false, colour: '#38b6e8' }] : []),
                  { lat: obs.lat, lon: obs.lon, label: 'you', arcs: false }]}
          onMove={(la, lo) => setObs({ lat: la, lon: lo })} />
      </div>

      {/* WHY THE SUN NEEDS TWO READINGS — founder's question, answered by the instrument. */}
      <div className="shell-picker lab-toggles">
        <span className="g-label" style={{ alignSelf: 'center' }}>Second sight</span>
        {[0, 1, 2, 4, 6].map((h) => (
          <button key={h} onClick={() => setGap(h)}
            className={`g-btn ${gap === h ? 'g-btn-primary' : ''}`}>
            {h === 0 ? 'none' : `+${h}h`}
          </button>
        ))}
      </div>

      {/* THE NOON SIGHT — founder: "generally second mates take noon site". Correct, and it is the
          one sight that needs no tables at all: at meridian passage LHA is zero, the sun bears due
          north or south, and the latitude falls straight out of the zenith distance and declination.
          A running fix is the general method; THIS is the daily one. */}
      {up && (
        <div className="lab-task">
          <div className="g-label">The noon sight — what a Second Mate actually takes</div>

          {/* ── THE HANG ──────────────────────────────────────────────────────────────────────
              Founder: "explain how latitude gets straight and calculated, and how (sun neither
              rises nor falls in sextant) and make him feel". You cannot feel that from a sentence.
              So: scrub the minutes, watch the number stop moving, and read what it cost in arc. */}
          <p className="g-body">
            Bring her down to the horizon and hold her there. Work the sextant as the morning goes
            on and you keep having to <b>raise</b> the altitude — she is climbing. Then she stops.
            Move the slider and watch the reading.
          </p>

          <div className="lab-scrub">
            <input type="range" min={-30} max={30} step={1} value={tmin}
              onChange={(e) => setTmin(Number(e.target.value))} className="lab-range" />
            <div className="lab-readout lab-sail">
              <div><span className="g-label">Time from transit</span>
                   <span className="lab-num">{tmin === 0 ? 'NOON' :
                     `${tmin > 0 ? '+' : '−'}${Math.abs(tmin)} min`}</span></div>
              <div><span className="g-label">Sextant altitude</span>
                   <span className="lab-num lab-cel">{altdm(altNow)}</span></div>
              <div><span className="g-label">Below her maximum</span>
                   <span className="lab-num">{dropMin < 0.05 ? '0.0′' : `${dropMin.toFixed(1)}′`}</span></div>
            </div>
          </div>

          <div className="lab-result">
            <Value of={computed(
              `In the two minutes before transit she rises ${Math.abs(twoMin).toFixed(2)} minutes of ` +
              `arc — about ${(Math.abs(twoMin) * 60).toFixed(0)} seconds of arc, on an instrument you ` +
              `read to a tenth of a minute from a moving deck. That is why she "neither rises nor ` +
              `falls": not because the sun stops, but because near her maximum the rate of change is ` +
              `ZERO and the altitude is changing as the SQUARE of the time either side.\n\n` +
              `You do not need the clock to be right. You watch her until she stops rising, take that ` +
              `reading, and that is your meridian altitude. The sun tells you when noon is.`)} />
          </div>

          <p className="g-body lab-why">
            That is the whole trick, and it is why this sight survived when everything else needed a
            chronometer. A longitude sight is ruined by four seconds of clock error. A noon latitude
            does not care what time it is — because at the moment you take it, time has stopped
            mattering.
          </p>

          {/* ── AND NOW WHERE THE LATITUDE COMES FROM ─────────────────────────────────────────
              One meridian, drawn edge-on. Every quantity is an ARC on that one circle, which is
              precisely why they add. */}
          <div className="lab-merfig">
            <MeridianFigure lat={obs.lat} dec={gpLat} zd={90 - altPeak} />
          </div>

          <div className="lab-result">
            <Value of={computed(
              `Look at the figure — it is your meridian, seen edge-on, and NOTHING on it is off that ` +
              `circle. Your zenith is straight up from you. The sun is ${(90 - altPeak).toFixed(1)}° ` +
              `from your zenith, so its geographical position is ${(90 - altPeak).toFixed(1)}° along ` +
              `the meridian from you. The GP sits at the declination, ${dm(gpLat, 'lat')}, because ` +
              `that is what declination MEANS.\n\n` +
              `Latitude is also an arc of that same meridian, from the Equator to you. So the two ` +
              `arcs simply join end to end:\n\n` +
              `    ZENITH DISTANCE ${(90 - altPeak).toFixed(1)}°  ` +
              `${obs.lat > gpLat ? '+' : '−'}  DECLINATION ${Math.abs(gpLat).toFixed(1)}°` +
              `  =  LATITUDE ${Math.abs(obs.lat).toFixed(1)}°\n\n` +
              `There is no table in that line, no interpolation, no LHA — because at transit the ` +
              `spherical triangle has collapsed flat onto the meridian and there is no triangle left ` +
              `to solve. That is why the noon latitude comes out STRAIGHT.`)} />
          </div>

          <p className="g-body lab-why">
            The sign is not a rule to memorise: it is which way round the arcs lie. If the sun bore
            SOUTH of you at noon its GP is south of you, so you are north of it and the zenith
            distance is ADDED to the declination. If she bore north, you are south of the GP and it
            comes off. Draw the two dots on the circle and the sign is obvious — which is the only
            way it survives an exam.
          </p>
          <div className="lab-result">
            <Value of={computed(
              `The sun crosses your meridian in ${merHrs.toFixed(1)} hours, at about ` +
              `${merUTC} UTC. At that instant LHA is zero, the sun bears due ` +
              `${obs.lat > gpLat ? 'SOUTH' : 'NORTH'} of you, and the spherical triangle collapses: ` +
              `there is no azimuth to work and no tables to enter.\n\n` +
              `LATITUDE = DECLINATION ${obs.lat > gpLat ? '+' : '−'} ZENITH DISTANCE. ` +
              `At your position that is ${dm(gpLat, 'lat')} ${obs.lat > gpLat ? '+' : '−'} ` +
              `${Math.abs(obs.lat - gpLat).toFixed(1)}° = ${dm(obs.lat, 'lat')}.`)} />
          </div>
          <p className="g-body lab-why">
            That is why it is the daily sight: one observation, no tables, and latitude to a mile. It
            gives you NO longitude on its own — for that you need the morning sun sight transferred up
            to noon, which is the running fix below. Morning sun run up to a noon latitude is the
            classic day's work.
          </p>
        </div>
      )}

      {b2 && (
        <div className="lab-task">
          <div className="g-label">Two circles, one fix</div>
          <div className="lab-result">
            <Value of={computed(
              `In ${gap} hours the sun's GP has run ${(gap * 15).toFixed(0)}° of longitude west, so its ` +
              `circle is somewhere else entirely. You are on BOTH circles, and they cross in two ` +
              `places — one of them nowhere near your dead reckoning. That crossing is the fix.\n\n` +
              `Azimuth ${Zn.toFixed(0)}° then ${Zn2.toFixed(0)}° — a cut of ${cut.toFixed(0)}°.`)} />
          </div>
          <div className="lab-result">
            <Value of={cut < 25 ? refused(
              `A cut of only ${cut.toFixed(0)}° is a poor fix. The two position lines are nearly ` +
              `parallel, so a small error in either throws the crossing a long way along them. Wait ` +
              `longer between sights and the azimuth opens out.`)
              : computed(`A cut of ${cut.toFixed(0)}° crosses well. Anything under about 25° is a ` +
                         `weak fix however carefully each sight was taken.`)} />
          </div>
          <p className="g-body lab-why">
            This is a RUNNING fix, and it costs something: the ship has moved between the two sights,
            so the first position line must be transferred forward for the course and distance made
            good before the crossing means anything. That transfer is the part a candidate gets wrong,
            and it is why a running fix is weaker than two bodies observed together at twilight.
          </p>
        </div>
      )}

      <div className="shell-picker lab-toggles">
        {['sun', 'moon'].map((k) => (
          <button key={k} onClick={() => setBody(k)}
            className={`g-btn ${body === k ? 'g-btn-primary' : ''}`}>{k}</button>
        ))}
      </div>

      <div className="lab-readout lab-sail">
        <div><span className="g-label">GP of the {body}</span>
             <span className="lab-num lab-gp">{dm(gpLat, 'lat')} {dm(gpLon, 'lon')}</span></div>
        <div><span className="g-label">Your position</span>
             <span className="lab-num">{dm(obs.lat, 'lat')} {dm(obs.lon, 'lon')}</span></div>
        <div><span className="g-label">LHA</span>
             <span className="lab-num">{((lha + 360) % 360).toFixed(1)}°</span></div>
        <div><span className="g-label">Calculated altitude Hc</span>
             <span className="lab-num lab-zen">{Hc.toFixed(1)}°</span></div>
        <div><span className="g-label">Zenith distance</span>
             <span className="lab-num">{zd.toFixed(1)}° = {zdNm.toFixed(0)} nm</span></div>
        <div><span className="g-label">Azimuth Zn</span>
             <span className="lab-num">{Zn.toFixed(1)}°</span></div>
      </div>

      <div className="lab-task">
        <div className="g-label">The circle</div>
        <div className="lab-result">
          <Value of={up ? computed(
            `The ${body} is ${Hc.toFixed(1)}° above your horizon, so you are ${zdNm.toFixed(0)} ` +
            `nautical miles from its GP — and you could be ANYWHERE on that circle. It is ` +
            `${(2 * Math.PI * zdNm).toFixed(0)} nm around. The short straight line you draw on a ` +
            `chart is a few miles of this arc, near enough to straight to use.`)
            : refused(`the ${body} is below your horizon from here — altitude ${Hc.toFixed(1)}°. ` +
                      `There is no circle to stand on, which is itself the answer: you cannot ` +
                      `observe a body you cannot see.`)} />
        </div>
      </div>

      {/* The almanac, cited to its own gate rather than asserted. */}
      <div className="lab-task">
        <div className="g-label">Where these came from</div>
        {/* SAID OUT LOUD, not hidden in the citation. A cited value carries its source in the chip's
            tooltip, and there is no hover on a phone — which is the only device that will ever be
            offline at sea. A candidate a thousand miles out, watching the app work with no signal,
            should be TOLD why it still works rather than left to wonder whether he is reading
            something stale. */}
        {almq.computedLocally && (
          <p className="g-body lab-offline">
            <b>No signal — this sight was computed on your phone.</b> The VSOP87 series that answer
            on the server travel inside the app, so the almanac needs no connection and has no year
            in which it runs out. The figures are the same ones, checked across both browser engines
            to better than a ten-millionth of a minute of arc.
          </p>
        )}
        <div className="lab-result">
          <Value of={cited(
            `${body} GHA ${b?.gha_dm || '—'}, dec ${b?.dec_dm || '—'} at ${alm?.utc || 'now'}`,
            /* WHERE it was computed is part of where it came from. Offline the same series run on
               this device, and saying so is not a disclaimer — a candidate working a sight with no
               signal should be able to see that the figure did not come from anywhere. */
            almq.computedLocally
              ? 'computed ON THIS DEVICE · VSOP87, the same series the server runs — gated across '
                + 'both engines to better than 1e-8′, no network involved'
              : '/api/almanac · VSOP87, gated against an independent implementation to better than 0.2′')} />
        </div>
        <div className="lab-result">
          <Value of={refused(
            'This is the CALCULATED altitude for the position on screen. A real sight is an OBSERVED ' +
            'altitude corrected for index error, dip, refraction, semi-diameter and parallax, and the ' +
            'INTERCEPT is the difference between the two — toward the body if the observed is greater. ' +
            'Those corrections are their own chapter and are not applied here, so this lab shows the ' +
            'geometry of the method and does not pretend to have taken a sight.')} />
        </div>
      </div>

      <div className="lab-def">
        <Value of={cited(
          'The geographical position of a celestial body is that point on the earth’s surface at '
          + 'which the body is in the zenith; and since the zenith distance is the angular distance '
          + 'from the zenith, the observer is at that same angular distance from the geographical '
          + 'position, upon a circle of equal altitude having the geographical position as its centre.',
          'Bowditch, The American Practical Navigator (1888) — public domain')} />
        <p className="g-body lab-why">
          That circle is the one on the globe above. Two bodies give two circles, and they cross in
          two places — one of which is obviously not where you are. That crossing is the fix.
        </p>
      </div>
    </div>
  );
}
